import React, { useState } from "react";
import { Lock, ShieldCheck, EyeOff, KeyRound, Copy, Check, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { generateVaultKey, wrapVaultKey, generateRecoveryCode, normalizeRecoveryCode, KDF_ITERATIONS } from "@/lib/vaultCrypto";
import { toast } from "sonner";

const MIN_LEN = 10;

export default function VaultSetup({ onCreated }) {
  const [step, setStep] = useState(1);
  const [pass, setPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState("");
  const [copied, setCopied] = useState(false);
  const [savedAck, setSavedAck] = useState(false);
  const [keyMaterial, setKeyMaterial] = useState(null);

  const strength = pass.length >= 16 ? "Strong" : pass.length >= MIN_LEN ? "Okay" : "Too short";

  const createKeys = async (e) => {
    e.preventDefault();
    if (pass.length < MIN_LEN) return toast.error(`Passphrase must be at least ${MIN_LEN} characters`);
    if (pass !== confirm) return toast.error("Passphrases don't match");
    setBusy(true);
    try {
      const vaultKey = await generateVaultKey();
      const code = generateRecoveryCode();
      const p = await wrapVaultKey(vaultKey, pass);
      const r = await wrapVaultKey(vaultKey, normalizeRecoveryCode(code));
      setKeyMaterial({ vaultKey, payload: {
        kdf_salt: p.salt, kdf_iterations: KDF_ITERATIONS, wrapped_key: p.wrapped, wrapped_key_iv: p.iv,
        recovery_salt: r.salt, recovery_wrapped_key: r.wrapped, recovery_wrapped_key_iv: r.iv,
      } });
      setRecovery(code);
      setStep(2);
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    setBusy(true);
    try {
      await api.post("/vault", keyMaterial.payload);
      toast.success("Your vault is ready");
      onCreated(keyMaterial.vaultKey);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create vault");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(recovery);
    } catch {
      window.prompt("Copy your recovery code:", recovery);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="vault-setup">
      <div className="bg-slate-900 text-white rounded-2xl p-7 sm:p-9 lb-grain">
        <p className="font-mono text-xs uppercase tracking-widest text-emerald-400 mb-2 flex items-center gap-1.5"><Lock size={13} /> Documentation Vault</p>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight" style={{ fontWeight: 900 }}>Your private record.<br />Readable by you alone.</h1>
        <p className="text-slate-300 mt-4 leading-relaxed">
          Keep a dated, chronological log of harassment, discrimination, retaliation, or anything you may need to prove later.
          Everything is encrypted in your browser before it leaves your device — my360boss staff, admins, and even a court order for our database cannot read it.
        </p>
        <div className="grid sm:grid-cols-3 gap-3 mt-6 text-sm">
          {[[ShieldCheck, "End-to-end encrypted"], [EyeOff, "Never shown to anyone"], [KeyRound, "Only your passphrase opens it"]].map(([I, t]) => (
            <div key={t} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2"><I size={15} className="text-emerald-400" /> {t}</div>
          ))}
        </div>
      </div>

      {step === 1 ? (
        <form onSubmit={createKeys} className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 mt-6 space-y-5" data-testid="vault-setup-form">
          <div>
            <h2 className="font-display text-2xl text-slate-900" style={{ fontWeight: 800 }}>Create your vault passphrase</h2>
            <p className="text-sm text-slate-500 mt-1">This is separate from your login password. A sentence you'll remember works best.</p>
          </div>
          <div>
            <Label htmlFor="vault-pass">Passphrase</Label>
            <Input id="vault-pass" data-testid="vault-passphrase-input" type="password" value={pass} onChange={(e) => setPass(e.target.value)} className="mt-1" autoComplete="new-password" />
            <p className={`text-xs mt-1 font-mono ${pass.length >= MIN_LEN ? "text-emerald-600" : "text-slate-400"}`} data-testid="vault-passphrase-strength">{pass ? strength : `At least ${MIN_LEN} characters`}</p>
          </div>
          <div>
            <Label htmlFor="vault-confirm">Confirm passphrase</Label>
            <Input id="vault-confirm" data-testid="vault-passphrase-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1" autoComplete="new-password" />
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
            <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />
            <label className="flex items-start gap-2.5 cursor-pointer text-sm text-amber-900 leading-relaxed">
              <Checkbox data-testid="vault-ack-checkbox" checked={ack} onCheckedChange={setAck} className="mt-0.5" />
              I understand that if I lose both my passphrase and my recovery code, my entries are permanently unrecoverable. Nobody can reset this for me.
            </label>
          </div>
          <Button data-testid="vault-create-btn" type="submit" disabled={!ack || busy} className="w-full h-12 bg-slate-900 hover:bg-emerald-600 rounded-full text-base">
            {busy ? "Generating keys..." : "Continue"}
          </Button>
        </form>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 mt-6 space-y-5" data-testid="vault-recovery-step">
          <div>
            <h2 className="font-display text-2xl text-slate-900" style={{ fontWeight: 800 }}>Save your recovery code</h2>
            <p className="text-sm text-slate-500 mt-1">If you forget your passphrase, this code is the only way back in. Write it down or store it in a password manager — not on a work device.</p>
          </div>
          <div className="bg-slate-900 text-emerald-300 font-mono text-xl sm:text-2xl tracking-widest text-center rounded-xl py-5 px-4 select-all" data-testid="vault-recovery-code">{recovery}</div>
          <Button type="button" variant="outline" onClick={copy} className="w-full rounded-full gap-2" data-testid="vault-copy-recovery-btn">
            {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copied" : "Copy code"}
          </Button>
          <label className="flex items-start gap-2.5 cursor-pointer text-sm text-slate-700">
            <Checkbox data-testid="vault-saved-recovery-checkbox" checked={savedAck} onCheckedChange={setSavedAck} className="mt-0.5" />
            I've saved my recovery code somewhere safe.
          </label>
          <Button data-testid="vault-finish-btn" onClick={finish} disabled={!savedAck || busy} className="w-full h-12 bg-slate-900 hover:bg-emerald-600 rounded-full text-base">
            {busy ? "Creating vault..." : "Open my vault"}
          </Button>
        </div>
      )}
    </div>
  );
}
